"use client"

import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import PasswordInput from "../components/auth/PasswordInput";
import { validatePassword, getPasswordStrength } from "../utils/auth/passwordUtils";
import { AuthStatus, type PasswordStrength, type PasswordValidation, type ResetPasswordFormData } from "../types/auth";
import ThemeToggle from "../components/auth/ThemeToggle";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { resetUserPassword } from "../redux/slices/authSlice";
import { toast } from "react-toastify";
import Loading from "../components/auth/Loading";

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { status, error } = useAppSelector((state) => state.auth);

  // Retrieve the email passed from the forgot password page
  const email = (location.state as { email: string } | null)?.email || "";

  const [formData, setFormData] = useState<ResetPasswordFormData>({
    otp: "",
    password: "",
    confirmPassword: "",
  });

  const [passwordsMatch, setPasswordsMatch] = useState<boolean>(true);
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>(null);
  const [validation, setValidation] = useState<PasswordValidation>({
    hasMinLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false,
  });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!formData.password) {
      setPasswordStrength(null);
      setValidation({
        hasMinLength: false,
        hasUpperCase: false,
        hasLowerCase: false,
        hasNumber: false,
        hasSpecialChar: false,
      });
      return;
    }

    const newValidation = validatePassword(formData.password);
    setValidation(newValidation);
    setPasswordStrength(getPasswordStrength(newValidation));

    if (formData.confirmPassword) {
      setPasswordsMatch(formData.password === formData.confirmPassword);
    }
  }, [formData.password, formData.confirmPassword]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };


  useEffect(() => {
    if (error) {
      if (typeof error === 'string') {
        toast.error(error)
      }
      else {
        toast.error((error as unknown as { message: string }).message)
      }
    }
  }, [error])

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!passwordsMatch || passwordStrength === "weak") {
      return;
    }
    setSubmitted(true);
    try {
      await dispatch(
        resetUserPassword({
          email,
          otp: formData.otp,
          newPassword: formData.password,
          confirmPassword: formData.confirmPassword,
        })
      ).unwrap();

      toast.success("Password resetted successfully")
      // After successful reset, wait 3 seconds then navigate to login.
    } catch (error) {
      toast.error("Failed to reset password")
    }
  };

  // After reset is successful and submitted, navigate to login after 3 seconds.
  useEffect(() => {
    if (submitted && status === "succeeded") {
      const timeout = setTimeout(() => {
        navigate("/login");
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [submitted, status, navigate]);

  const getPasswordStrengthBarStyles = () => {
    if (!formData.password) {
      return {
        width: "0%",
        backgroundColor: "transparent",
      };
    }
    let width = "0%";
    let color = "bg-gray-500";
    switch (passwordStrength) {
      case "weak":
        width = "33%";
        color = "bg-red-500";
        break;
      case "medium":
        width = "66%";
        color = "bg-yellow-500";
        break;
      case "strong":
        width = "100%";
        color = "bg-green-500";
        break;
    }
    return { width, className: color };
  };

  const strengthBarStyles = getPasswordStrengthBarStyles();

  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-[#261d57] text-gray-900 dark:text-white transition-all">
      <ThemeToggle />
      <div className="w-full max-w-6xl h-auto sm:h-[600px] rounded-2xl overflow-hidden flex flex-col sm:flex-row">
        <div className="hidden min-h-screen sm:flex sm:w-1/2 flex-col justify-between">
          <img src="/landing/login.png" className="w-full min-h-screen" alt="Background" />
        </div>
        <div className="w-full sm:w-1/2 p-6 rounded-2xl bg-gray-100 dark:bg-gray-800">
          <div className="flex flex-col items-center mb-6">
            <img src="./logo/green-logo.png" className="w-12 h-12 object-cover" alt="Logo" />
            <h1 className="text-xl font-bold mt-2 text-gray-900 dark:text-white">Reset Password</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-1 text-sm font-normal text-center">
              Create a new password for your account
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 w-full px-4 sm:px-0">
            {/* OTP Input Field */}
            <div>
              <PasswordInput
                id="otp"
                value={formData.otp}
                onChange={handleChange}
                placeholder="Enter OTP"
                required
              />
            </div>
            <div>
              <PasswordInput
                id="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter new password"
                required
              />
              <div className="mt-2">
                <div className="w-full h-2 dark:bg-gray-700 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${strengthBarStyles.className} transition-all duration-300`}
                    style={{ width: strengthBarStyles.width }}
                  ></div>
                </div>
              </div>
            </div>
            <div>
              <PasswordInput
                id="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm new password"
                hasError={!passwordsMatch && formData.confirmPassword.length > 0}
                error={!passwordsMatch && formData.confirmPassword ? "Passwords do not match" : ""}
                required
              />
            </div>
            <button
              type="submit"
              className={`w-full font-medium py-2.5 px-4 rounded-md transition duration-150 ease-in-out mt-6 disabled:opacity-90 disabled:cursor-not-allowed ${passwordsMatch && passwordStrength === "strong"
                ? "bg-[#3FBBA3] hover:bg-[#3FBBA3] text-white cursor-pointer"
                : "bg-[#3FBBA3] text-white cursor-not-allowed"
                }`}
              disabled={!passwordsMatch || passwordStrength !== "strong" || status === "loading"}
            >
              {
                (status === AuthStatus.LOADING)
                  ?
                  <Loading text="Resetting password, please wait..." />
                  :
                  "Reset Password"
              }
            </button>
          </form>
          <p className="mt-1 text-center text-sm text-gray-400">
            <Link to="/login" className="text-[#3FBBA3] hover:text-[#3FBBA3]">
              Back to Login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
